/**
 * Shared Lightweight Charts zoom / pan interaction helpers.
 * TradingView-like: wheel zoom, Shift+wheel pan, pinch, kinetic drag, fit reset.
 */

/** Default createChart interaction options (merge into createChart opts). */
export const CHART_INTERACTION_OPTS = {
  handleScroll: {
    mouseWheel: false, // custom wheel: zoom or Shift+pan (see attachChartInteraction)
    pressedMouseMove: true,
    horzTouchDrag: true,
    vertTouchDrag: true,
  },
  handleScale: {
    mouseWheel: false, // handled in attachChartInteraction for cursor-centered zoom
    pinch: true,
    axisPressedMouseMove: { time: true, price: true },
    axisDoubleClickReset: { time: true, price: true },
  },
  kineticScroll: {
    mouse: true,
    touch: true,
  },
};

/** Sensible time-scale defaults for smooth zoom range. */
export const CHART_TIME_SCALE_OPTS = {
  rightOffset: 12,
  barSpacing: 8,
  minBarSpacing: 1.5,
  maxBarSpacing: 48,
  fixLeftEdge: false,
  fixRightEdge: false,
  lockVisibleTimeRangeOnResize: true,
  shiftVisibleRangeOnNewBar: true,
};

const MIN_LOGICAL_SPAN = 4;
const MAX_LOGICAL_SPAN = 800;

/**
 * Zoom visible logical range around center (or optional anchor 0–1 within view).
 * factor < 1 zooms in, > 1 zooms out.
 */
export function zoomLogical(chart, factor, anchor = 0.5) {
  if (!chart || !Number.isFinite(factor) || factor <= 0) return;
  const ts = chart.timeScale();
  let range;
  try {
    range = ts.getVisibleLogicalRange();
  } catch (_) {
    return;
  }
  if (!range || !Number.isFinite(range.from) || !Number.isFinite(range.to)) return;

  const span = range.to - range.from;
  if (span <= 0) return;

  let newSpan = span * factor;
  newSpan = Math.max(MIN_LOGICAL_SPAN, Math.min(MAX_LOGICAL_SPAN, newSpan));
  const a = Math.max(0, Math.min(1, anchor));
  const center = range.from + span * a;
  const from = center - newSpan * a;
  const to = from + newSpan;
  try {
    ts.setVisibleLogicalRange({ from, to });
  } catch (_) { /* empty / destroyed */ }
}

/** Shift the visible window by N logical bars (positive = look at newer / right). */
export function panLogical(chart, bars) {
  if (!chart || !Number.isFinite(bars) || bars === 0) return;
  const ts = chart.timeScale();
  let range;
  try {
    range = ts.getVisibleLogicalRange();
  } catch (_) {
    return;
  }
  if (!range || !Number.isFinite(range.from) || !Number.isFinite(range.to)) return;
  try {
    ts.setVisibleLogicalRange({
      from: range.from + bars,
      to: range.to + bars,
    });
  } catch (_) { /* noop */ }
}

/** Fit all data in view. */
export function fitChart(chart) {
  if (!chart) return;
  try {
    chart.timeScale().fitContent();
  } catch (_) { /* noop */ }
}

/**
 * Reset price scale autoScale (right axis) after manual axis zoom.
 */
export function resetPriceScale(chart, priceScaleId = 'right') {
  if (!chart) return;
  try {
    chart.priceScale(priceScaleId).applyOptions({ autoScale: true });
  } catch (_) { /* noop */ }
}

/**
 * Attach enhanced wheel / dblclick / keyboard interaction to a chart container.
 * Returns a cleanup function.
 *
 * Wheel:
 *   - default          → zoom toward cursor X (time)
 *   - Shift + wheel    → horizontal pan
 *   - Ctrl/Cmd + wheel → finer zoom
 * Dblclick chart body → fitContent + reset price autoScale
 * Keys (when container focused or hovered): +/− zoom, arrows pan, 0 / Home fit
 */
export function attachChartInteraction(chart, el, options = {}) {
  if (!chart || !el) return () => {};

  const {
    zoomStep = 0.12,
    fineZoomStep = 0.05,
    panBars = 3,
    enableKeyboard = true,
    enableDblClickFit = true,
    priceScaleId = 'right',
  } = options;

  el.tabIndex = el.tabIndex >= 0 ? el.tabIndex : 0;
  el.style.outline = el.style.outline || 'none';
  el.style.touchAction = 'none';
  el.style.overscrollBehavior = 'contain';

  const isOver = { current: false };

  const onEnter = () => { isOver.current = true; };
  const onLeave = () => { isOver.current = false; };

  const logicalAnchorFromClientX = (clientX) => {
    const rect = el.getBoundingClientRect();
    if (!rect.width) return 0.5;
    // Approximate: chart plot is full width minus right price scale (~56px)
    const plotW = Math.max(1, rect.width - 56);
    const x = clientX - rect.left;
    return Math.max(0, Math.min(1, x / plotW));
  };

  const onWheel = (e) => {
    // Always take wheel when over chart so the page does not scroll
    e.preventDefault();
    e.stopPropagation();

    const dy = e.deltaY;
    if (!dy) return;

    // Shift + wheel → pan (TradingView-style)
    if (e.shiftKey) {
      const range = chart.timeScale().getVisibleLogicalRange?.();
      const span = range ? Math.max(1, range.to - range.from) : 40;
      // Normalize delta across devices (lines / pixels)
      const unit = e.deltaMode === 1 ? 20 : e.deltaMode === 2 ? 80 : 1;
      const px = dy * unit;
      const bars = (px / Math.max(el.clientWidth, 1)) * span * 1.25;
      panLogical(chart, bars);
      return;
    }

    // Wheel zoom toward cursor
    const step = (e.ctrlKey || e.metaKey) ? fineZoomStep : zoomStep;
    // deltaY > 0 → zoom out
    const factor = dy > 0 ? (1 + step) : (1 - step);
    const anchor = logicalAnchorFromClientX(e.clientX);
    zoomLogical(chart, factor, anchor);
  };

  const onDblClick = (e) => {
    if (!enableDblClickFit) return;
    // Ignore double-clicks that LWC uses on axes (they still bubble sometimes)
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    // Skip far-right price scale strip & bottom time scale
    if (x > rect.width - 52 || y > rect.height - 28) return;
    fitChart(chart);
    resetPriceScale(chart, priceScaleId);
  };

  const onKeyDown = (e) => {
    if (!enableKeyboard) return;
    if (!isOver.current && document.activeElement !== el) return;
    // Don't steal keys from inputs
    const tag = (e.target && e.target.tagName) || '';
    if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;

    const key = e.key;
    if (key === '+' || key === '=' || key === 'Add') {
      e.preventDefault();
      zoomLogical(chart, 1 - zoomStep, 0.5);
    } else if (key === '-' || key === '_' || key === 'Subtract') {
      e.preventDefault();
      zoomLogical(chart, 1 + zoomStep, 0.5);
    } else if (key === 'ArrowLeft') {
      e.preventDefault();
      panLogical(chart, -panBars * (e.shiftKey ? 4 : 1));
    } else if (key === 'ArrowRight') {
      e.preventDefault();
      panLogical(chart, panBars * (e.shiftKey ? 4 : 1));
    } else if (key === 'Home' || key === '0') {
      e.preventDefault();
      fitChart(chart);
      resetPriceScale(chart, priceScaleId);
    }
  };

  el.addEventListener('pointerenter', onEnter);
  el.addEventListener('pointerleave', onLeave);
  el.addEventListener('wheel', onWheel, { passive: false });
  if (enableDblClickFit) el.addEventListener('dblclick', onDblClick);
  if (enableKeyboard) window.addEventListener('keydown', onKeyDown);

  return () => {
    el.removeEventListener('pointerenter', onEnter);
    el.removeEventListener('pointerleave', onLeave);
    el.removeEventListener('wheel', onWheel);
    if (enableDblClickFit) el.removeEventListener('dblclick', onDblClick);
    if (enableKeyboard) window.removeEventListener('keydown', onKeyDown);
  };
}
